/**
 * FloatingUserMenu 설정
 * 
 * 레벨별 메뉴 아이템 정의
 * 중복 코드를 제거하고 선언적으로 메뉴 구성
 */
import {
    HomeIcon,
    UserIcon,
    CalendarIcon,
    StatisticsIcon,
    ReservationIcon,
    SettingsIcon,
    GuideIcon,
    AdminIcon,
    PublicCalendarIcon,
    SpecialIcon,
    StudioManagementIcon,
    FlashBookIcon,
    LogoutIcon
} from '../icons/MenuIcons';
import {
    PERMISSIONS,
    VISIBILITY_RULES,
    SPECIAL_USERS,
    checkMenuItemAccess
} from '../../utils/permissionConfig';

// 다국어 텍스트 설정
export const MENU_TEXT = {
    home: { ko: '홈', en: 'HOME', fr: 'ACCUEIL' },
    me: { ko: '나', en: 'ME', fr: 'MOI' },
    dm: { ko: '메시지', en: 'DM', fr: 'MSG' },
    calendar: { ko: '달력', en: 'CALENDAR', fr: 'CALENDRIER' },
    publicCalendar: { ko: '공개캘린더', en: 'PUBLIC CALENDAR', fr: 'CALENDRIER PUBLIC' },
    statistics: { ko: '통계', en: 'STATS', fr: 'STATS' },
    rdv: { ko: '예약', en: 'RDV', fr: 'RDV' },
    myReservation: { ko: '내 예약', en: 'MY RESERVATION', fr: 'MA RÉSERVATION' },
    guide: { ko: '가이드', en: 'GUIDE', fr: 'GUIDE' },
    reglage: { ko: '설정', en: 'SETTINGS', fr: 'RÉGLAGES' },
    logout: { ko: '로그아웃', en: 'LOGOUT', fr: 'DÉCONNEXION' },
    admin: { ko: '관리자페이지', en: 'ADMIN', fr: 'ADMIN' },
    special: { ko: '특별메뉴', en: 'SPECIAL', fr: 'SPÉCIAL' },
    reservationList: { ko: '예약 목록', en: 'RESERVATION LIST', fr: 'LISTE DES RÉSERVATIONS' },
    myReservations: { ko: '내 예약', en: 'MY RESERVATIONS', fr: 'MES RÉSERVATIONS' },
    studioManagement: { ko: '스튜디오관리', en: 'STUDIO', fr: 'STUDIO' },
    flashbook: { ko: '플래시북', en: 'FLASHBOOK', fr: 'FLASHBOOK' },
};

// 서브메뉴 다국어 텍스트 (FloatingUserMenu.jsx에서 이동)
export const SUBMENU_TEXT = {
    // STUDIO 서브메뉴
    stocks: { ko: '재고', en: 'STOCKS', fr: 'STOCKS' },
    commissions: { ko: '커미션', en: 'COMMISSIONS', fr: 'COMMISSIONS' },
    parameters: { ko: '설정', en: 'PARAMETERS', fr: 'PARAMÈTRES' },
    users: { ko: '회원관리', en: 'USERS', fr: 'UTILISATEURS' },
    staffArtists: { ko: '스태프관리', en: 'STAFF ARTISTS', fr: 'ARTISTES STAFF' },
    guestArtists: { ko: '게스트관리', en: 'GUEST ARTISTS', fr: 'ARTISTES INVITÉS' },
    expenses: { ko: '지출기록', en: 'EXPENSES', fr: 'DÉPENSES' },

    // REGLAGE 서브메뉴
    languageTheme: { ko: '언어/테마', en: 'LANGUAGE/THEME', fr: 'LANGUE/THÈME' },
    personalInfo: { ko: '개인정보', en: 'PERSONAL INFO', fr: 'INFOS PERSO' },
    appInstallation: { ko: '앱 설치 안내', en: 'APP INSTALLATION', fr: 'INSTALLATION DE L’APP' },
    help: { ko: '도움말', en: 'HELP', fr: 'AIDE' },
    special: { ko: 'SPECIAL', en: 'SPECIAL', fr: 'SPÉCIAL' },
    registerUser: { ko: '회원등록', en: 'REGISTER USER', fr: 'INSCRIRE UTILISATEUR' },
};

// 스튜디오 서브메뉴 설정 (FloatingUserMenu.jsx에서 이동)
export const STUDIO_SUBMENU = [
    { id: 'stocks', textKey: 'stocks', path: '/stock-management', enabled: true },
    { id: 'commissions', textKey: 'commissions', path: '/commission-management', enabled: true },
    { id: 'parameters', textKey: 'parameters', path: null, enabled: false },
    { id: 'users', textKey: 'users', path: '/admin/users', enabled: true, showOnlyFor: [PERMISSIONS.ADMIN_ACCESS] },
    { id: 'staffArtists', textKey: 'staffArtists', path: '/admin/staff-artists', enabled: true, showOnlyFor: [PERMISSIONS.ADMIN_ACCESS] },
    { id: 'guestArtists', textKey: 'guestArtists', path: '/admin/guest-artists', enabled: true, showOnlyFor: [PERMISSIONS.ADMIN_ACCESS] },
    { id: 'registerUser', textKey: 'registerUser', path: '/register', enabled: true, showOnlyFor: [PERMISSIONS.ADMIN_ACCESS] },
    { id: 'expenses', textKey: 'expenses', path: '/expenses', enabled: true, showOnlyFor: [PERMISSIONS.EXPENSES_ACCESS] },
];

// REGLAGE 서브메뉴 설정 (언어/테마, 개인정보, 도움말, SPECIAL)
export const SETTINGS_SUBMENU = [
    { id: 'languageTheme', textKey: 'languageTheme', path: '/settings', enabled: true },
    { id: 'personalInfo', textKey: 'personalInfo', path: '/me', enabled: true },
    { id: 'appInstallation', textKey: 'appInstallation', path: '/install-app', enabled: true },
    { id: 'help', textKey: 'help', path: '/guide', enabled: true },
    { id: 'special', textKey: 'special', path: '/special', enabled: true, showOnlyFor: [PERMISSIONS.CHANNEL_ADMIN] },
];

export { SPECIAL_USERS, checkMenuItemAccess, PERMISSIONS, VISIBILITY_RULES };

// 메뉴 아이템 정의
const createMenuItem = (id, path, textKey, Icon, options = {}) => ({
    id,
    path,
    textKey,
    Icon,
    useIcon: options.useIcon !== false, // 기본값 true
    useText: options.useText || false,  // 기본값 false (아이콘만 표시)
    showOnlyFor: options.showOnlyFor || null, // 특정 레벨에서만 표시
    hideFor: options.hideFor || null, // 특정 레벨에서 숨김
    isDynamic: options.isDynamic || false, // 동적 경로 여부
    getPath: options.getPath || null, // 동적 경로 생성 함수
    isAction: options.isAction || false, // 액션 버튼 여부 (로그아웃 등)
    hasSubmenu: options.hasSubmenu || false, // 서브메뉴 보유 여부
});

// 공통 메뉴 아이템들
const COMMON_MENU_ITEMS = {
    home: createMenuItem('home', '/client', 'home', HomeIcon),
    homeGuest: createMenuItem('home', '/guest', 'home', HomeIcon),
    me: createMenuItem('me', '/MyPage', 'me', UserIcon),
    meAlt: createMenuItem('me', '/me', 'me', UserIcon),
    dm: createMenuItem('dm', '/dm', 'dm', null, { useIcon: false, useText: true }),
    calendar: createMenuItem('calendar', '/calendar', 'calendar', CalendarIcon),
    calendarGuest: createMenuItem('calendar', '/guest/calendar', 'calendar', CalendarIcon),
    publicCalendar: createMenuItem('publicCalendar', null, 'publicCalendar', PublicCalendarIcon, {
        isDynamic: true,
        getPath: (userInfo) => `/public-calendar?artist=${userInfo?.user_name}`,
        showOnlyFor: [VISIBILITY_RULES.STAFF_ARTIST],
    }),
    statistics: createMenuItem('statistics', '/artist/reservations', 'statistics', StatisticsIcon),
    reservationList: createMenuItem('reservationList', '/manual-reservations', 'reservationList', null, { useIcon: false, useText: true }),
    reservationListArtist: createMenuItem('reservationList', '/artist/reservations/list', 'reservationList', null, { useIcon: false, useText: true }),
    reservationIcon: createMenuItem('rdv', '/manual-reservations', 'rdv', ReservationIcon),
    reservationGuest: createMenuItem('rdv', '/guest/reservations', 'rdv', ReservationIcon),
    myReservations: createMenuItem('myReservations', '/client/reservations', 'myReservations', null, { useIcon: false, useText: true }),
    settings: createMenuItem('settings', '/settings', 'reglage', SettingsIcon, {
        hasSubmenu: true, // 서브메뉴 보유 (언어/테마, 개인정보, 도움말)
    }),
    guide: createMenuItem('guide', '/guide', 'guide', GuideIcon),
    guideGuest: createMenuItem('guide', '/guest/guide', 'guide', GuideIcon),
    admin: createMenuItem('admin', '/admin', 'admin', AdminIcon),
    special: createMenuItem('special', '/special', 'special', SpecialIcon, {
        showOnlyFor: [PERMISSIONS.CHANNEL_ADMIN]
    }),
    studioManagement: createMenuItem('studioManagement', '/studio-management', 'studioManagement', StudioManagementIcon, {
        showOnlyFor: [VISIBILITY_RULES.COMMISSION_ACCESS],
        hasSubmenu: true, // 서브메뉴 보유
    }),
    flashbook: createMenuItem('flashbook', '/flashbook', 'flashbook', FlashBookIcon),
    logout: createMenuItem('logout', null, 'logout', LogoutIcon, { isAction: true }),
};

// 레벨별 메뉴 설정
// 순서: home → calendar → rdv → stats → (public) → settings → guide → (admin) → special → me → logout
export const MENU_CONFIG = {
    admin: [
        COMMON_MENU_ITEMS.home,
        COMMON_MENU_ITEMS.calendar,
        COMMON_MENU_ITEMS.reservationList,
        COMMON_MENU_ITEMS.statistics,
        COMMON_MENU_ITEMS.publicCalendar,
        COMMON_MENU_ITEMS.settings,
        COMMON_MENU_ITEMS.admin,
        COMMON_MENU_ITEMS.logout,
    ],
    artist: [
        COMMON_MENU_ITEMS.home,
        COMMON_MENU_ITEMS.calendar,
        COMMON_MENU_ITEMS.reservationList, // Changed from reservationListArtist - now uses /manual-reservations
        COMMON_MENU_ITEMS.statistics,
        COMMON_MENU_ITEMS.publicCalendar,
        COMMON_MENU_ITEMS.settings,
        COMMON_MENU_ITEMS.logout,
    ],
    client: [
        COMMON_MENU_ITEMS.home,
        // [ARCHIVED] myReservations removed - ClientReservationPage moved to Archive
        COMMON_MENU_ITEMS.settings,
        COMMON_MENU_ITEMS.logout,
    ],
    staff_artist: [
        COMMON_MENU_ITEMS.home,
        COMMON_MENU_ITEMS.calendar,
        COMMON_MENU_ITEMS.reservationIcon,
        COMMON_MENU_ITEMS.statistics,
        COMMON_MENU_ITEMS.flashbook,
        COMMON_MENU_ITEMS.publicCalendar,
        COMMON_MENU_ITEMS.settings,
        COMMON_MENU_ITEMS.studioManagement,
        COMMON_MENU_ITEMS.logout,
    ],
    guest_artist: [
        COMMON_MENU_ITEMS.homeGuest,
        COMMON_MENU_ITEMS.meAlt,
        COMMON_MENU_ITEMS.calendarGuest,
        // reservationGuest (rdv) 제거 - 게스트 아티스트에게 예약 메뉴 숨김
        COMMON_MENU_ITEMS.guideGuest,
        COMMON_MENU_ITEMS.settings,
        COMMON_MENU_ITEMS.logout,
    ],
    guest: [
        COMMON_MENU_ITEMS.home,
        COMMON_MENU_ITEMS.me,
        COMMON_MENU_ITEMS.publicCalendar,
        COMMON_MENU_ITEMS.logout,
    ],
};

// 기본 메뉴 (어떤 레벨에도 해당하지 않을 때)
export const DEFAULT_MENU = MENU_CONFIG.guest;

// 메뉴 가져오기 함수
export const getMenuForLevel = (level) => {
    return MENU_CONFIG[level] || DEFAULT_MENU;
};
